import type {
  CreateSeasonRequest,
  CreateTeamRequest,
  DepartmentView,
  RolloverPreviewView,
  RolloverRequest,
  SeasonStatus,
  SeasonView,
  SupportedLocale,
} from '@stm/contracts';
import { isCalendarDate } from '@stm/contracts';
import { catalogs, translate, type TranslationKey } from '@stm/i18n';

export const seasonStatusKeys: Readonly<Record<SeasonStatus, TranslationKey>> = {
  draft: 'teams.season.status.draft',
  active: 'teams.season.status.active',
  closed: 'teams.season.status.closed',
};

const categoryKeys: ReadonlySet<string> = new Set(
  Object.keys(catalogs.en).filter((key) => key.startsWith('teams.category.')),
);

/** A category code without a translation (for example one added later) is shown as written. */
export function categoryLabel(locale: SupportedLocale, code: string): string {
  const key = `teams.category.${code}`;
  return categoryKeys.has(key) ? translate(locale, key as TranslationKey) : code;
}

/**
 * Keeps the season the person is looking at while it still exists; otherwise the newest active
 * season, then the newest one. The list arrives newest first.
 */
export function chooseSeason(
  seasons: readonly SeasonView[],
  current: string | null,
): string | null {
  if (current !== null && seasons.some((season) => season.id === current)) return current;
  return (seasons.find((season) => season.status === 'active') ?? seasons[0])?.id ?? null;
}

export function canManageDepartment(
  departments: readonly DepartmentView[],
  departmentId: string,
): boolean {
  return departments.some((department) => department.id === departmentId && department.canManage);
}

export function structurePath(
  base: string,
  collection: 'seasons' | 'teams' | 'training-groups',
  seasonId?: string,
): string {
  return seasonId === undefined
    ? `${base}/${collection}`
    : `${base}/${collection}?seasonId=${encodeURIComponent(seasonId)}`;
}

export interface SeasonForm {
  departmentId: string;
  label: string;
  startsOn: string;
  endsOn: string;
}

export function emptySeasonForm(departments: readonly DepartmentView[]): SeasonForm {
  const manageable = departments.find((department) => department.canManage);
  return { departmentId: manageable?.id ?? '', label: '', startsOn: '', endsOn: '' };
}

export function canSubmitSeason(form: SeasonForm): boolean {
  return (
    form.departmentId !== '' &&
    form.label.trim() !== '' &&
    isCalendarDate(form.startsOn) &&
    isCalendarDate(form.endsOn) &&
    form.endsOn >= form.startsOn
  );
}

export function seasonRequestBody(form: SeasonForm): CreateSeasonRequest {
  return {
    departmentId: form.departmentId,
    label: form.label,
    startsOn: form.startsOn,
    endsOn: form.endsOn,
  };
}

export interface TeamForm {
  name: string;
  categoryCode: string;
  competitionDivision: string;
  teamOrdinal: string;
}

export function emptyTeamForm(categoryCodes: readonly string[]): TeamForm {
  return {
    name: '',
    categoryCode: categoryCodes[0] ?? '',
    competitionDivision: '',
    teamOrdinal: '1',
  };
}

export function canSubmitTeam(form: TeamForm): boolean {
  const ordinal = Number(form.teamOrdinal);
  return (
    form.name.trim() !== '' &&
    form.categoryCode !== '' &&
    Number.isInteger(ordinal) &&
    ordinal >= 1 &&
    ordinal <= 20
  );
}

export function teamRequestBody(seasonId: string, form: TeamForm): CreateTeamRequest {
  const division = form.competitionDivision.trim();
  return {
    seasonId,
    categoryCode: form.categoryCode,
    name: form.name,
    competitionDivision: division === '' ? null : division,
    teamOrdinal: Number(form.teamOrdinal),
  };
}

/** A shirt number is optional; an empty or invalid entry means none rather than a failed request. */
export function shirtNumberFrom(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const number = Number(trimmed);
  return Number.isInteger(number) && number >= 0 && number <= 99 ? number : null;
}

export function isValidShirtEntry(text: string): boolean {
  return text.trim() === '' || shirtNumberFrom(text) !== null;
}

/** The same calendar day a year later; 29 February becomes 28 February when the next year has none. */
export function sameDayNextYear(isoDate: string): string {
  const [year = 0, month = 1, day = 1] = isoDate.split('-').map(Number);
  const next = new Date(Date.UTC(year + 1, month - 1, day));
  if (next.getUTCMonth() !== month - 1) next.setUTCDate(0);
  return next.toISOString().slice(0, 10);
}

export interface RolloverTeamChoice {
  sourceTeamId: string;
  selected: boolean;
  name: string;
  categoryCode: string;
  activeMembers: number;
}

export interface RolloverGroupChoice {
  sourceGroupId: string;
  selected: boolean;
  name: string;
  activeMembers: number;
}

export interface RolloverForm {
  label: string;
  startsOn: string;
  endsOn: string;
  carryOverMembers: boolean;
  teams: RolloverTeamChoice[];
  groups: RolloverGroupChoice[];
}

/** Starts from the proposal: every team and group selected, dates one year on, nobody carried over. */
export function rolloverFormFrom(preview: RolloverPreviewView): RolloverForm {
  return {
    label: '',
    startsOn: sameDayNextYear(preview.sourceSeason.startsOn),
    endsOn: sameDayNextYear(preview.sourceSeason.endsOn),
    carryOverMembers: false,
    teams: preview.teams.map((team) => ({
      sourceTeamId: team.sourceTeamId,
      selected: true,
      name: team.name,
      categoryCode: team.categoryCode,
      activeMembers: team.activeMembers,
    })),
    groups: preview.groups.map((group) => ({
      sourceGroupId: group.sourceGroupId,
      selected: true,
      name: group.name,
      activeMembers: group.activeMembers,
    })),
  };
}

export function canSubmitRollover(form: RolloverForm): boolean {
  const chosenTeams = form.teams.filter((team) => team.selected);
  const chosenGroups = form.groups.filter((group) => group.selected);
  return (
    form.label.trim() !== '' &&
    isCalendarDate(form.startsOn) &&
    isCalendarDate(form.endsOn) &&
    form.endsOn >= form.startsOn &&
    chosenTeams.every((team) => team.name.trim() !== '' && team.categoryCode !== '') &&
    chosenGroups.every((group) => group.name.trim() !== '')
  );
}

/** Members the rollover would place: the active athletes of the selected teams and groups. */
export function carriedMemberCount(form: RolloverForm): number {
  return (
    form.teams.filter((team) => team.selected).reduce((sum, team) => sum + team.activeMembers, 0) +
    form.groups
      .filter((group) => group.selected)
      .reduce((sum, group) => sum + group.activeMembers, 0)
  );
}

export function rolloverRequestBody(form: RolloverForm): RolloverRequest {
  return {
    label: form.label,
    startsOn: form.startsOn,
    endsOn: form.endsOn,
    carryOverMembers: form.carryOverMembers,
    teams: form.teams
      .filter((team) => team.selected)
      .map((team) => ({
        sourceTeamId: team.sourceTeamId,
        name: team.name,
        categoryCode: team.categoryCode,
      })),
    groups: form.groups
      .filter((group) => group.selected)
      .map((group) => ({ sourceGroupId: group.sourceGroupId, name: group.name })),
  };
}
